import React from "react";
import { Check, Copy, FileCode2, FileSpreadsheet, ImageDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const EXT_BY_LANG = {
    abap: "abap", sql: "sql", json: "json", xml: "xml", javascript: "js", js: "js",
    typescript: "ts", python: "py", py: "py", bash: "sh", shell: "sh", yaml: "yaml", html: "html",
};

const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

/** Pulls ```fenced``` code blocks out of a reply. */
export function extractCodeBlocks(text = "") {
    const out = [];
    const re = /```([a-zA-Z0-9+#-]*)\n([\s\S]*?)```/g;
    let m;
    while ((m = re.exec(text)) !== null) {
        const lang = (m[1] || "").toLowerCase();
        const code = m[2].replace(/\s+$/, "");
        if (code.trim()) out.push({ lang, code });
    }
    return out;
}

/** Pulls markdown pipe-tables out of a reply. */
export function extractTables(text = "") {
    const lines = (text || "").split("\n");
    const tables = [];
    let i = 0;
    while (i < lines.length) {
        const isRow = (l) => l.trim().startsWith("|") && l.includes("|", 1);
        const isSep = (l) => /^\s*\|?[\s:-]*-[-\s:|]*\|?\s*$/.test(l) && l.includes("-");
        if (isRow(lines[i]) && i + 1 < lines.length && isSep(lines[i + 1])) {
            const cells = (l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
            const headers = cells(lines[i]);
            const rows = [];
            let j = i + 2;
            while (j < lines.length && isRow(lines[j])) {
                rows.push(cells(lines[j]));
                j += 1;
            }
            if (rows.length) tables.push({ headers, rows });
            i = j;
        } else {
            i += 1;
        }
    }
    return tables;
}

/** Per-message download toolbar: copy the reply, pull code blocks out as real
 * source files, any table out as a workbook, and generated images as PNG. */
export function MessageArtifacts({ msg, index }) {
    const [copied, setCopied] = React.useState(false);
    const codeBlocks = React.useMemo(() => extractCodeBlocks(msg.text), [msg.text]);
    const tables = React.useMemo(() => extractTables(msg.text), [msg.text]);
    const hasImage = Boolean(msg.image_base64);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(msg.text || "");
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (e) {
            toast.error("Clipboard blocked by the browser");
        }
    };

    const downloadCode = (block, j) => {
        const ext = EXT_BY_LANG[block.lang] || "txt";
        saveBlob(new Blob([block.code], { type: "text/plain;charset=utf-8" }), `utsav_snippet_${index}_${j + 1}.${ext}`);
        toast.success(`Saved as .${ext}`);
    };

    const downloadTable = async (table, j) => {
        try {
            const res = await api.post(
                "/utsav/export/table",
                { title: `Utsav Table ${j + 1}`, headers: table.headers, rows: table.rows },
                { responseType: "blob" },
            );
            saveBlob(new Blob([res.data]), `utsav_table_${index}_${j + 1}.xlsx`);
            toast.success("Table exported to .xlsx");
        } catch (e) {
            toast.error("Table export failed");
        }
    };

    const downloadImage = () => {
        const bin = atob(msg.image_base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
        saveBlob(new Blob([bytes], { type: msg.mime_type || "image/png" }), `utsav_image_${index}.png`);
    };

    if (!msg.text && !hasImage) return null;

    return (
        <div className="mt-2.5 pt-2 border-t border-white/5 flex flex-wrap items-center gap-1.5">
            <button
                data-testid={TID.utsavMsgCopy(index)}
                onClick={copy}
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-cyan-300 transition-colors"
            >
                {copied ? <Check size={10} /> : <Copy size={10} />} {copied ? "copied" : "copy"}
            </button>
            {codeBlocks.map((b, j) => (
                <button
                    key={`code-${j}`}
                    data-testid={TID.utsavMsgCode(index, j)}
                    onClick={() => downloadCode(b, j)}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-rose-300 transition-colors"
                >
                    <FileCode2 size={10} /> code {codeBlocks.length > 1 ? j + 1 : ""}
                    <span className="text-slate-400">.{EXT_BY_LANG[b.lang] || "txt"}</span>
                </button>
            ))}
            {tables.map((t, j) => (
                <button
                    key={`table-${j}`}
                    data-testid={TID.utsavMsgTable(index, j)}
                    onClick={() => downloadTable(t, j)}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-emerald-300 transition-colors"
                >
                    <FileSpreadsheet size={10} /> table {tables.length > 1 ? j + 1 : ""} .xlsx
                </button>
            ))}
            {hasImage && (
                <button
                    data-testid={TID.utsavMsgImage(index)}
                    onClick={downloadImage}
                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-cyan-300 transition-colors"
                >
                    <ImageDown size={10} /> image .png
                </button>
            )}
        </div>
    );
}
